import { useEffect, useMemo, useState } from "react"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Search,
  Trash2,
} from "lucide-react"
import { FcGoogle } from "react-icons/fc"
import { FaFacebook } from "react-icons/fa"
import { AiOutlineApple } from "react-icons/ai"
import { RiLockPasswordFill } from "react-icons/ri"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useContactList,
  useDeleteContact,
  type ContactSubmission,
} from "@/lib/queries/contact"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Contact Inbox (list + view)
// LEGACY: apps/admin/src/pages/Contact.jsx
// API:    POST /admin/list_user_contact   (server pagination/sort/search)
//         POST /admin/delete_user_contact { ids: [id] }
//
// LIST request body:
//   page, limit, search?, sortyBy?, sortDirection?    (sortyBy typo PRESERVED)
//
// LIST columns (same set as legacy; subject + message hidden from the row
// to match legacy behavior — they show in the View modal):
//   name        → "Name" (sortable)
//   email       → "Email" (sortable)
//   View        → opens modal with name + email + subject + message + auth provider icon
//   Delete      → POST /admin/delete_user_contact { ids: [_id] }
//   created_at  → "Received" (sortable)
//
// IA NOTE: legacy used JSON-stringified DOM ids on the View / Delete
// buttons (passing the entire row payload through e.target.id). v2 keeps
// the row in component state instead — same behavior, no JSON.parse needed.
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function AuthProviderIcon({ type }: { type?: string }) {
  if (type === "google") return <FcGoogle size={20} aria-label="Google" />
  if (type === "facebook")
    return <FaFacebook size={20} className="text-[#1877f2]" aria-label="Facebook" />
  if (type === "apple")
    return <AiOutlineApple size={20} className="text-foreground" aria-label="Apple" />
  return (
    <RiLockPasswordFill
      size={20}
      className="text-muted-foreground"
      aria-label="Email/password"
    />
  )
}

export default function ContactInbox() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const contactQ = useContactList(params)
  const deleteMut = useDeleteContact()

  const [viewContact, setViewContact] = useState<ContactSubmission | null>(null)
  const [deleteContact, setDeleteContact] = useState<ContactSubmission | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = contactQ.data?.totalPages ?? 0
  const totalDocs = contactQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteContact) return
    await deleteMut.mutateAsync(deleteContact._id)
    setDeleteContact(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Contact inbox</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} message${totalDocs === 1 ? "" : "s"}`
                : "Messages sent through the in-app contact form"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search messages…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search messages"
              />
            </div>
            <ExportButton<ContactSubmission>
              filenamePrefix="contact-messages"
              totalEstimate={totalDocs}
              columns={[
                { label: "Name", get: (c) => c.name },
                { label: "Email", get: (c) => c.email },
                { label: "Subject", get: (c) => c.subject },
                { label: "Message", get: (c) => c.message },
                { label: "Auth Provider", get: (c) => c.auth_type },
                { label: "Received", get: (c) => c.created_at },
                { label: "ID", get: (c) => c._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/list_user_contact", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {contactQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load messages. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH field="name" label="Name" {...{ onSortClick, SortIcon }} />
                <SortableTH field="email" label="Email" {...{ onSortClick, SortIcon }} />
                <SortableTH field="created_at" label="Received" {...{ onSortClick, SortIcon }} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contactQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!contactQ.isLoading && (contactQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                    No messages match your filters.
                  </TableCell>
                </TableRow>
              )}
              {contactQ.data?.docs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell className="whitespace-nowrap font-medium">
                    {c.name || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {c.email || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(c.created_at)}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewContact(c)}
                      aria-label="View message"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteContact(c)}
                      aria-label="Delete message"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* View modal — name / email / subject / message / auth provider */}
      <Dialog
        open={!!viewContact}
        onOpenChange={(open) => !open && setViewContact(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Message details</DialogTitle>
            <DialogDescription>
              Submitted {formatFullDate(viewContact?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {viewContact && (
            <div className="space-y-3 text-sm">
              <Field label="Name" value={viewContact.name || "—"} />
              <Field label="Email" value={viewContact.email || "—"} />
              <Field label="Subject" value={viewContact.subject || "—"} />
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Message
                </div>
                <div className="mt-1 whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
                  {viewContact.message || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Auth provider
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <AuthProviderIcon type={viewContact.auth_type} />
                  <span className="text-muted-foreground">
                    {viewContact.auth_type || "unknown"}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {viewContact && (
              <a
                // Replies should leave a trail on the shared support
                // inbox (hi@goodtalk.coach) — CC it so support@ has a
                // copy regardless of which admin replies from their
                // personal email. Subject is pre-filled with the
                // original so the user sees a threaded reply, and body
                // quotes their message for context.
                href={(() => {
                  const to = viewContact.email ?? ""
                  const cc = "hi@goodtalk.coach"
                  const subj = viewContact.subject
                    ? `Re: ${viewContact.subject}`
                    : "Re: Your GoodTalk message"
                  const greeting = viewContact.name
                    ? `Hi ${viewContact.name},\n\n`
                    : "Hi,\n\n"
                  const quoted = (viewContact.message || "")
                    .split("\n")
                    .map((l) => `> ${l}`)
                    .join("\n")
                  const body = `${greeting}\n\n— GoodTalk Support\nhi@goodtalk.coach\n\n--- Your original message ---\n${quoted}`
                  const params = new URLSearchParams({
                    cc,
                    subject: subj,
                    body,
                  })
                  return `mailto:${to}?${params.toString()}`
                })()}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Reply via email
              </a>
            )}
            <Button variant="outline" onClick={() => setViewContact(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete modal */}
      <Dialog
        open={!!deleteContact}
        onOpenChange={(open) => !open && setDeleteContact(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete message?</DialogTitle>
            <DialogDescription>
              Message from {deleteContact?.name || "this user"} will be
              removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteContact(null)}
              disabled={deleteMut.isPending}
            >
              Keep message
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words">{value}</div>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
